import type { SimpleTableSetupResult } from "../SimpleTableSetup";
import type { BaseEntity } from "../types";

/**
 * How the SimpleTable context reads FilterContext.
 *
 * Split out of `SimpleTableContextValue.ts` (module-size lane L17). Behaviour is
 * pinned by `tests/universal/simple-table-context-value.test.ts`.
 *
 * NOTE ON THE REMOVED "SAFETY CHECK": the pre-split code opened with
 *
 *     const safeFilterContext = filterContext || { state: {...}, actions: {...} };
 *
 * which was dead code hiding a crash. `useFilterContext` THROWS when its
 * provider is missing, so `setup.filterContext` is non-nullable and that branch
 * is unreachable — and the object it fell back TO had no `filters` key, which
 * the very next line (`safeFilterContext?.filters.search`) dereferences. Had it
 * ever been reached it would have thrown a TypeError instead of degrading. The
 * `uiState` / `actions` defaults below are kept because, unlike that one, they
 * are well-formed and complete.
 */

type FilterContextOf<TData extends BaseEntity> = SimpleTableSetupResult<TData>["filterContext"];

/** Shape of the filter UI state the table reads (operator + advanced-panel flag). */
export const DEFAULT_FILTER_UI_STATE = {
  mode: "simple" as const,
  isOpen: false,
  showAdvancedFilters: false,
  filterOperator: "AND" as const,
  customRangeModal: {
    isOpen: false,
    fieldId: "",
    fieldLabel: "",
  },
};

/** No-op action set, used only if a provider ever ships an incomplete value. */
export const DEFAULT_FILTER_ACTIONS = {
  setSearchTerm: () => {},
  setSimpleFilter: () => {},
  setAdvancedFilters: () => {},
  setFilterOperator: () => {},
  toggleMode: () => {},
  togglePanel: () => {},
  setShowAdvancedFilters: () => {},
  setCustomRangeModal: () => {},
  clearAll: () => {},
  applyPreset: () => {},
};

export interface FilterProjection<TData extends BaseEntity> {
  /** `uiState` drives the OPERATOR and the advanced-panel toggle, not `filters`. */
  readonly uiState: FilterContextOf<TData>["uiState"] | typeof DEFAULT_FILTER_UI_STATE;
  readonly actions: FilterContextOf<TData>["actions"] | typeof DEFAULT_FILTER_ACTIONS;
  readonly searchTerm: string;
  readonly simpleFilters: FilterContextOf<TData>["filters"]["simple"];
  readonly advancedFilters: FilterContextOf<TData>["filters"]["advanced"];
}

/**
 * Flatten FilterContext into the handful of values the table's state slice
 * needs, so the slice never reaches through nested properties itself.
 */
export function projectFilterContext<TData extends BaseEntity>(
  filterContext: FilterContextOf<TData>
): FilterProjection<TData> {
  return {
    uiState: filterContext.uiState || DEFAULT_FILTER_UI_STATE,
    actions: filterContext.actions || DEFAULT_FILTER_ACTIONS,
    searchTerm: filterContext.filters.search || "",
    simpleFilters: filterContext.filters.simple || {},
    advancedFilters: filterContext.filters.advanced || [],
  };
}
